import React from "react";

import { HseDetailsNotice } from "components/ui/HseDetailsNotice/HseDetailsNotice";
import { DeceasedPatientNotice } from "features/cdm/components/DeceasedPatientNotice/DeceasedPatientNotice";
import { MissingPcrsDetailsNotice } from "features/cdm/components/MissingPcrsDetailsNotice/MissingPcrsDetailsNotice";
import { NonIrishCountryNotice } from "features/cdm/components/NonIrishCountryNotice/NonIrishCountryNotice";
import { PCRSMidMonthNotice } from "features/cdm/components/PCRSMidMonthNotice/PCRSMidMonthNotice";
import { PCRSNotOnPanelNotice } from "features/cdm/components/PCRSNotOnPanelNotice/PCRSNotOnPanelNotice";
import { PCRSSchemeSkippedNotice } from "features/cdm/components/PCRSSchemeSkippedNotice/PCRSSchemeSkippedNotice";

// The stacked PCRS informational notices shown above the CDM content. Each is
// gated on already-computed flags (see useCdmGating) plus the scheme-check
// "skipped" case (private patient — no scheme check performed).
//
// A deceased patient (or a non-Irish country) short-circuits the rest: no review
// can be started or edited, so the PCRS timing/eligibility advice ("consider
// waiting until next month", "submit once the transfer takes effect") is
// irrelevant noise and reads absurdly next to a blocking eligibility notice.
export const CdmNotices = ({
  isContentReady,
  schemeCheckSkipped,
  showMidMonthNotice,
  showNotOnPanelNotice,
  showDeceasedNotice,
  showNonIrishNotice,
  isMissingRequiredPcrsDetails,
  missingRequiredPcrsDetails,
  patient,
  patientId,
  isMissingHseDetails,
}) => {
  if (!isContentReady) return null;

  if (showDeceasedNotice) return <DeceasedPatientNotice />;

  if (showNonIrishNotice) return <NonIrishCountryNotice />;

  // The patient record is missing a detail the CDM message is built from:
  // XmlCommonGenerator reads `patient.getDateOfBirth().format(...)` for PID.7 and
  // `patient.getGender().substring(0, 1)` for PID.8, both of which NPE on a null
  // (KIN-1733). Blocking, and it short-circuits the PCRS timing advice for the same
  // reason the branches around it do — no review can proceed, so that advice is noise.
  if (isMissingHseDetails) {
    return (
      <HseDetailsNotice
        patient={patient}
        patientId={patientId}
        submissionName="to the HSE"
      />
    );
  }

  // Patient not on this GP's panel — a blocking eligibility error. Reviews are
  // disabled, so it short-circuits the amber PCRS timing advice (which would be
  // noise / read absurdly next to a blocking notice).
  if (showNotOnPanelNotice) return <PCRSNotOnPanelNotice />;

  // A blocking error (required PCRS details not returned) short-circuits the
  // amber PCRS timing advice — the review can't proceed, so that advice is noise.
  if (isMissingRequiredPcrsDetails) {
    return (
      <MissingPcrsDetailsNotice missingFields={missingRequiredPcrsDetails} />
    );
  }

  return (
    <>
      {schemeCheckSkipped && <PCRSSchemeSkippedNotice />}
      {showMidMonthNotice && <PCRSMidMonthNotice />}
    </>
  );
};
